import { useState } from 'react';
import { useExperiment } from '~/systems/experiment';
import { useTitleRef } from '~/systems/title';
import IconButton from '../IconButton/IconButton';
import RenamingInput from '../editor/RenamingInput/RenamingInput';
import './ExperimentTabs.css';

interface ExperimentTabsProps {
  isCreatingNewExperiment: boolean;
  isSyncingState: boolean;
  onNewExperiment: (yn: boolean) => void;
}

function ClickToRenameSpan({
  name,
  onClick
}: {
  name: string;
  onClick: () => void;
}) {
  const ref = useTitleRef<HTMLButtonElement>('Click to rename');
  return (
    <button
      ref={ref}
      className="ExperimentTabs-label"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {name}
    </button>
  );
}

export default function ExperimentTabs({
  isCreatingNewExperiment,
  isSyncingState,
  onNewExperiment
}: ExperimentTabsProps) {
  const {
    closeExperiment,
    currentExperimentIndex,
    experiments,
    openTransaction,
    setCurrentExperiment
  } = useExperiment();

  const [renaming, setRenaming] = useState(-1);

  function handleSaveNewName(newNameValue: string) {
    const tx = openTransaction('changeName');
    tx.push({ kind: 'rename', name: newNameValue });
    tx.commit();
    setRenaming(-1);
  }

  function handleOpenRenaming(index: number) {
    setRenaming(index);
  }

  function handleCancelRenaming() {
    setRenaming(-1);
  }

  function handleSwitchExperiment(index: number) {
    setCurrentExperiment(index);
    onNewExperiment(false);
  }

  return (
    <div className="ExperimentTabs">
      <div className="ExperimentTabs-tabBar">
        {experiments.map((exp, index) => (
          <div
            key={index}
            className="ExperimentTabs-tab"
            aria-selected={
              !isCreatingNewExperiment && currentExperimentIndex === index
            }
            role="tab"
            tabIndex={0}
            onClick={() => handleSwitchExperiment(index)}
            onKeyDown={(e) => {
              // Ignore keys bubbling up from children, like the renaming input
              if (e.target !== e.currentTarget) {
                return;
              }
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSwitchExperiment(index);
              }
            }}
          >
            {renaming === index ? (
              <RenamingInput
                initialName={exp.name}
                onSave={handleSaveNewName}
                onCancel={handleCancelRenaming}
              />
            ) : !isCreatingNewExperiment && currentExperimentIndex === index ? (
              <ClickToRenameSpan
                name={exp.name}
                onClick={() => handleOpenRenaming(index)}
              />
            ) : (
              <span className="ExperimentTabs-label">{exp.name}</span>
            )}
            {!isCreatingNewExperiment &&
              currentExperimentIndex === index &&
              renaming !== index && (
                <IconButton
                  icon="xmark"
                  label="Close experiment"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeExperiment(index);
                  }}
                />
              )}
          </div>
        ))}
        <IconButton
          aria-selected={isCreatingNewExperiment}
          icon="plus"
          className="ExperimentTabs-tab is-newTab"
          label="Add an experiment"
          onClick={() => onNewExperiment(true)}
        />
        {isSyncingState && <div data-testid="experiment-state-sync" />}
      </div>
    </div>
  );
}
